// src/pages/admin/JobDetailPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { db, storage } from "../../services/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { getBlob, ref } from "firebase/storage";
import {
  FUTILE_CHARGE_AMOUNT,
  linkify,
  addrToText,
  esc,
  hasValue,
  firstDefined,
  asNumber,
  isSilicaMaterialName,
  getQty,
  fmtDateTime,
  fmtCurrency,
  sourceLabel,
} from "./jobDetail/helpers";
import MetricCard from "./jobDetail/MetricCard";
import ItemList from "./jobDetail/ItemList";

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location || {};
  const stopFromState = state?.stop || null;

  const qs = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search]
  );
  const backDate = state?.date || qs.get("date") || "";
  const backDriver = state?.driverId || qs.get("driverId") || "";
  const backRunKey = state?.runKey || qs.get("runKey") || "";

  const buildBackHref = () => {
    const p = new URLSearchParams();
    p.set("tab", "view-history");
    if (backDate) p.set("date", backDate);
    if (backDriver) p.set("driverId", backDriver);
    if (backRunKey) p.set("runKey", backRunKey);
    return `/dashboard?${p.toString()}`;
  };

  const [jobDoc, setJobDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zipping, setZipping] = useState(false);
  const [confirmDownloading, setConfirmDownloading] = useState(false);
  const [emptyLoadRecordsOpen, setEmptyLoadRecordsOpen] = useState(false);
  const [loadDocs, setLoadDocs] = useState([]);
  const [confirmationSource, setConfirmationSource] = useState({
    url: "",
    path: "",
  });
  const [serviceDoc, setServiceDoc] = useState(null);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "jobs", jobId));
        if (!on) return;
        const nextJob = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setJobDoc(nextJob);

        const cc =
          nextJob?.clientConfirmation || nextJob?.customerConfirmation || {};
        const directSource = {
          url:
            nextJob?.clientConfirmationPdfUrl ||
            nextJob?.confirmationPdfUrl ||
            cc.pdfUrl ||
            cc.url ||
            "",
          path:
            nextJob?.clientConfirmationPdfPath ||
            nextJob?.confirmationPdfPath ||
            cc.pdfPath ||
            cc.path ||
            "",
        };

        if (directSource.url || directSource.path) {
          setConfirmationSource(directSource);
          return;
        }

        const trySubcollection = async (subName) => {
          try {
            const q = query(
              collection(db, "jobs", jobId, subName),
              orderBy("createdAt", "desc"),
              limit(1)
            );
            const s = await getDocs(q);
            if (!s.empty) {
              const data = s.docs[0].data();
              return {
                url: data.pdfUrl || data.url || "",
                path: data.pdfPath || data.path || "",
              };
            }
          } catch {}
          return { url: "", path: "" };
        };

        let resolved = await trySubcollection("clientConfirmations");
        if (!resolved.url && !resolved.path) {
          resolved = await trySubcollection("customerConfirmations");
        }
        if (!resolved.url && !resolved.path) {
          try {
            const fallback = await getDoc(doc(db, "serviceConfirmations", jobId));
            if (fallback.exists()) {
              const data = fallback.data();
              resolved = {
                url: data.pdfUrl || data.url || "",
                path: data.pdfPath || data.path || "",
              };
            }
          } catch {}
        }

        if (on) setConfirmationSource(resolved);
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => {
      on = false;
    };
  }, [jobId]);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const q = query(collection(db, "loads"), where("jobId", "==", jobId));
        const snap = await getDocs(q);
        if (!on) return;
        setLoadDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch {
        if (on) setLoadDocs([]);
      }
    })();
    return () => {
      on = false;
    };
  }, [jobId]);

  useEffect(() => {
    let on = true;
    (async () => {
      const sourceJob = { ...(stopFromState || {}), ...(jobDoc || {}) };
      const serviceId =
        sourceJob?.serviceId ||
        sourceJob?.service?.id ||
        sourceJob?.jobTypeId ||
        sourceJob?.jobType?.id ||
        "";

      try {
        if (serviceId) {
          const snap = await getDoc(doc(db, "jobTypes", serviceId));
          if (on && snap.exists()) {
            setServiceDoc({ id: snap.id, ...snap.data() });
            return;
          }
        }

        const serviceName = String(
          firstDefined(
            sourceJob?.serviceName,
            sourceJob?.service?.name,
            sourceJob?.jobTypeName,
            sourceJob?.jobType?.name,
            sourceJob?.jobType
          ) || ""
        )
          .trim()
          .toLowerCase();

        if (!serviceName) {
          if (on) setServiceDoc(null);
          return;
        }

        const snap = await getDocs(collection(db, "jobTypes"));
        const match = snap.docs.find((d) => {
          const data = d.data() || {};
          return String(data.name || "").trim().toLowerCase() === serviceName;
        });

        if (on) {
          setServiceDoc(match ? { id: match.id, ...match.data() } : null);
        }
      } catch {
        if (on) setServiceDoc(null);
      }
    })();

    return () => {
      on = false;
    };
  }, [jobDoc, stopFromState]);

  const job = useMemo(
    () => ({ ...(stopFromState || {}), ...(jobDoc || {}) }),
    [jobDoc, stopFromState]
  );

  const serviceName = useMemo(
    () =>
      firstDefined(
        job.serviceName,
        job.service?.name,
        job.jobTypeName,
        job.jobType?.name,
        job.jobType,
        serviceDoc?.name
      ) || "",
    [job, serviceDoc]
  );

  const serviceHasPrice = useMemo(() => {
    const explicitHasPrice = firstDefined(
      job.serviceHasPrice,
      job.service?.hasPrice,
      job.jobTypeHasPrice,
      serviceDoc?.hasPrice
    );

    if (typeof explicitHasPrice === "boolean") return explicitHasPrice;

    return hasValue(
      firstDefined(
        job.servicePrice,
        job.service?.price,
        job.jobTypePrice,
        job.price,
        serviceDoc?.price
      )
    );
  }, [job, serviceDoc]);

  const servicePrice = useMemo(
    () =>
      firstDefined(
        job.servicePrice,
        job.service?.price,
        job.jobTypePrice,
        job.price,
        serviceDoc?.price
      ),
    [job, serviceDoc]
  );

  const hasConfirmationPdf = Boolean(
    confirmationSource?.url || confirmationSource?.path
  );

  const deliveries = useMemo(
    () => (Array.isArray(job.deliveries) ? job.deliveries : []),
    [job]
  );
  const pickups = useMemo(
    () => (Array.isArray(job.pickups) ? job.pickups : []),
    [job]
  );

  const emptyLoads = useMemo(() => {
    const base = Array.isArray(job.emptyLoads) ? job.emptyLoads : [];
    const ledgerLoads = (loadDocs || []).map((l) => ({
      createdAt: l.createdAt || l.serviceDate || l.updatedAt,
      truckId: l.truckId || "",
      truckRego: l.truckRego || "",
      loadType: l.loadType || "",
      binId: l.binId || "",
      binName: l.binName || "",
      binCategoryId: l.binCategoryId || "",
      binCategoryName: l.binCategoryName || "",
      overload: !!l.overload,
      units: asNumber(l.units),
      materials: Array.isArray(l.materials) ? l.materials : [],
      silicaWeightKg: asNumber(l.silicaWeightKg),
      bagCount: asNumber(l.bagCount),
      chargeType: l.chargeType || "",
      chargeQuantity: asNumber(l.chargeQuantity),
      chargeUnitLabel: l.chargeUnitLabel || "",
      unitPrice: l.unitPrice ?? null,
      amount: l.amount ?? null,
      photoUrl: l.loadPhotoUrl || "",
      _source: "loads-ledger",
    }));

    return [...base, ...ledgerLoads];
  }, [job, loadDocs]);

  const emptyLoadTotals = useMemo(() => {
    const fromJob = job.emptyLoadTotals || {};
    const fromJobByMaterial = fromJob.byMaterial;
    const byMaterial =
      fromJobByMaterial && Object.keys(fromJobByMaterial).length
        ? fromJobByMaterial
        : emptyLoads.reduce((acc, load) => {
            const units = getQty(load);
            (Array.isArray(load.materials) ? load.materials : []).forEach((m) => {
              const percent = asNumber(m?.percent) / 100;
              const name = m?.name || "Unknown";
              acc[name] = (acc[name] || 0) + units * percent;
            });
            return acc;
          }, {});

    const uniqueLoads = Object.values(
      emptyLoads.reduce((acc, load, idx) => {
        const key = `${load.createdAt}_${load.binId || idx}`;
        acc[key] = load;
        return acc;
      }, {})
    );

    const totalUnits =
      typeof fromJob.totalUnits === "number"
        ? fromJob.totalUnits
        : uniqueLoads.reduce((sum, load) => sum + getQty(load), 0);

    const silicaSummary = fromJob?.silica || {
      weightKg: uniqueLoads.reduce((sum, load) => sum + asNumber(load.silicaWeightKg), 0),
      bagCount: uniqueLoads.reduce((sum, load) => sum + asNumber(load.bagCount), 0),
      amount: uniqueLoads.reduce(
        (sum, load) =>
          (Array.isArray(load.materials) ? load.materials : []).some((m) =>
            isSilicaMaterialName(m.name)
          )
            ? sum + asNumber(load.amount)
            : sum,
        0
      ),
      binCount: uniqueLoads.filter((load) =>
        (Array.isArray(load.materials) ? load.materials : []).some(
          (m) => isSilicaMaterialName(m.name)
        )
      ).length,
    };

    const silicaAmount =
      typeof silicaSummary.amount === "number"
        ? silicaSummary.amount
        : uniqueLoads.reduce(
            (sum, load) =>
              (Array.isArray(load.materials) ? load.materials : []).some((m) =>
                isSilicaMaterialName(m.name)
              )
                ? sum + asNumber(load.amount)
                : sum,
            0
          );

    return {
      totalUnits,
      byMaterial,
      silica: { ...silicaSummary, amount: silicaAmount },
    };
  }, [job, emptyLoads]);

  const deliveredTotal = useMemo(
    () =>
      asNumber(
        job.totalDelivered ?? deliveries.reduce((sum, item) => sum + getQty(item), 0)
      ),
    [job, deliveries]
  );

  const pickedUpTotal = useMemo(
    () =>
      asNumber(
        job.totalPickedUp ?? pickups.reduce((sum, item) => sum + getQty(item), 0)
      ),
    [job, pickups]
  );

  const emptiedTotal = useMemo(
    () => Math.round(asNumber(emptyLoadTotals.totalUnits) * 100) / 100,
    [emptyLoadTotals]
  );

  const serviceBinQty = useMemo(() => {
    const explicitQty = firstDefined(
      job.serviceBinQty,
      job.service?.binQty,
      job.jobTypeBinQty,
      job.billableBinQty
    );

    if (hasValue(explicitQty)) return asNumber(explicitQty);

    const candidates = [deliveredTotal, pickedUpTotal, emptiedTotal].filter(
      (value) => asNumber(value) > 0
    );

    if (candidates.length === 1) return candidates[0];

    return Math.max(deliveredTotal, pickedUpTotal, emptiedTotal, 0);
  }, [job, deliveredTotal, pickedUpTotal, emptiedTotal]);

  const serviceTotalPrice = useMemo(() => {
    if (!serviceHasPrice) return null;
    return Math.round(asNumber(servicePrice) * asNumber(serviceBinQty) * 100) / 100;
  }, [serviceHasPrice, servicePrice, serviceBinQty]);

  const isFutile = useMemo(
    () =>
      Boolean(job.futile) ||
      String(job.status || "").toLowerCase() === "futile" ||
      String(job.skipReason || "").toLowerCase() === "futile",
    [job]
  );

  const futileCharge = useMemo(() => {
    if (!isFutile) return 0;
    return (
      asNumber(
        firstDefined(
          job.futileCharge,
          job.futileAmount,
          stopFromState?.futileCharge,
          stopFromState?.futileAmount,
          FUTILE_CHARGE_AMOUNT
        )
      ) || FUTILE_CHARGE_AMOUNT
    );
  }, [isFutile, job, stopFromState]);

  const futileNote =
    firstDefined(job.futileNote, stopFromState?.futileNote, job.skipNote, stopFromState?.skipNote) ||
    "";

  const materialSummary = useMemo(() => {
    return Object.entries(emptyLoadTotals.byMaterial || {})
      .map(([name, units]) => ({
        name,
        units: Math.round(asNumber(units) * 100) / 100,
      }))
      .sort((a, b) => b.units - a.units);
  }, [emptyLoadTotals]);

  const jobProofPhotos = useMemo(() => {
    const arr = Array.isArray(job.proofPhotos) ? job.proofPhotos : [];
    return arr
      .map((p, i) => ({
        url: typeof p === "string" ? p : p?.url || "",
        name: p?.name || `proof_${i + 1}.jpg`,
        storagePath: p?.storagePath,
        uploadedAt: p?.uploadedAt,
        _source: "job",
      }))
      .filter((x) => x.url);
  }, [job]);

  const loadPhotos = useMemo(() => {
    const out = [];

    emptyLoads.forEach((l, li) => {
      if (l?.photoUrl) {
        out.push({
          url: l.photoUrl,
          name: `empty_load_${li + 1}.jpg`,
          uploadedAt: l.createdAt,
          _source: l.overload ? "overload" : "empty-load",
        });
      }
    });

    (loadDocs || []).forEach((l, li) => {
      const push = (url, idx, label) => {
        if (!url) return;
        out.push({
          url,
          name: `${label || "load"}_${li + 1}_${(idx ?? 0) + 1}.jpg`,
          uploadedAt: l.serviceDate || l.createdAt || l.updatedAt,
          _source: "emptied",
        });
      };

      if (Array.isArray(l.photos)) {
        l.photos.forEach((p, i) =>
          push(typeof p === "string" ? p : p?.url, i, "photos")
        );
      }
      if (Array.isArray(l.proofPhotos)) {
        l.proofPhotos.forEach((p, i) =>
          push(typeof p === "string" ? p : p?.url, i, "proof")
        );
      }
      if (l.loadPhotoUrl) push(l.loadPhotoUrl, 0, "loadPhotoUrl");
    });

    return out.filter((x) => x.url);
  }, [emptyLoads, loadDocs]);

  const allPhotos = useMemo(() => {
    const seen = new Set();
    return [...jobProofPhotos, ...loadPhotos].filter((p) => {
      if (seen.has(p.url)) return false;
      seen.add(p.url);
      return true;
    });
  }, [jobProofPhotos, loadPhotos]);

  const extractStoragePath = (url) => {
    try {
      const u = new URL(url);
      const i = u.pathname.indexOf("/o/");
      return i === -1 ? null : decodeURIComponent(u.pathname.slice(i + 3));
    } catch {
      return null;
    }
  };

  const getPhotoBlob = async (photo) => {
    if (photo?.storagePath) return getBlob(ref(storage, photo.storagePath));
    if (photo?.url) {
      const sp = extractStoragePath(photo.url);
      if (sp) return getBlob(ref(storage, sp));
      const r = await fetch(photo.url, { cache: "no-store" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.blob();
    }
    throw new Error("Invalid photo");
  };

  const downloadBlobAsFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  const downloadServiceConfirmationPdf = async () => {
    if (!jobDoc) return alert("Job not loaded yet.");
    const { url, path } = confirmationSource || {};
    if (!url && !path) {
      alert("No service confirmation PDF found for this job yet.");
      return;
    }

    setConfirmDownloading(true);
    try {
      const filename = `service-confirmation_${jobId}.pdf`;

      if (path) {
        const blob = await getBlob(ref(storage, path));
        downloadBlobAsFile(blob, filename);
        return;
      }

      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error("fetch failed");
        const blob = await res.blob();
        downloadBlobAsFile(blob, filename);
      } catch {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("Download confirmation failed:", err);
      alert("Failed to download confirmation PDF.");
    } finally {
      setConfirmDownloading(false);
    }
  };

  const downloadAllPhotos = async () => {
    if (!allPhotos.length) return alert("No photos to download.");
    setZipping(true);
    try {
      const JSZip = (await import("jszip")).default;
      const { saveAs } = await import("file-saver");
      const zip = new JSZip();
      const folder = zip.folder("photos");

      for (let i = 0; i < allPhotos.length; i += 1) {
        const photo = allPhotos[i];
        const base = photo.name || `photo_${i + 1}.jpg`;
        const safe = `${photo._source || "photo"}_${base}`.replace(
          /[^\w.\- ]+/g,
          "_"
        );
        try {
          folder.file(safe, await getPhotoBlob(photo));
        } catch (e) {
          console.error("Photo error", e);
        }
      }

      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, `job_${jobId}_photos.zip`);
    } finally {
      setZipping(false);
    }
  };

  const exportOneJobCSV = () => {
    const headers = [
      "JobId",
      "Address",
      "Customer",
      "Site",
      "PO",
      "Description",
      "Service",
      "ServiceUnitPrice",
      "ServiceBinQty",
      "ServiceTotalPrice",
      "Futile",
      "FutileCharge",
      "FutileNote",
      "StartTrip",
      "JobStart",
      "Finished",
      "Delivered",
      "PickedUp",
      "EmptyUnits",
      "SilicaWeightKg",
      "SilicaBags",
      "SilicaAmount",
      "DriverNotes",
      "PhotoURL",
    ];

    const siteName =
      job.siteLabel || job.siteName || job.address || job.customerName || "";
    const siteText = addrToText(siteName);
    const addrText = addrToText(job.address);
    const driverNotes = jobDoc?.driverNotes || "";

    const photoCell = allPhotos
      .map((p, i) => (p.url ? `=HYPERLINK("${p.url}", "Photo ${i + 1}")` : ""))
      .filter(Boolean)
      .join(" ; ");

    const row = [
      jobId,
      addrText,
      job.customerName || "",
      siteText,
      job.poNumber || "",
      job.description || "",
      serviceName || "",
      serviceHasPrice ? Math.round(asNumber(servicePrice) * 100) / 100 : "No price",
      Math.round(asNumber(serviceBinQty) * 100) / 100,
      serviceHasPrice ? Math.round(asNumber(serviceTotalPrice) * 100) / 100 : "No price",
      isFutile ? "Yes" : "No",
      isFutile ? Math.round(asNumber(futileCharge) * 100) / 100 : "",
      isFutile ? futileNote : "",
      job.startTripAt || "",
      job.startJobAt || job.arrivedAt || "",
      job.completedTime || "",
      asNumber(
        job.totalDelivered ?? deliveries.reduce((sum, item) => sum + getQty(item), 0)
      ),
      asNumber(
        job.totalPickedUp ?? pickups.reduce((sum, item) => sum + getQty(item), 0)
      ),
      Math.round(asNumber(emptyLoadTotals.totalUnits) * 100) / 100,
      Math.round(asNumber(emptyLoadTotals?.silica?.weightKg) * 100) / 100,
      asNumber(emptyLoadTotals?.silica?.bagCount),
      Math.round(asNumber(emptyLoadTotals?.silica?.amount) * 100) / 100,
      driverNotes,
      photoCell,
    ];

    const csv = [headers, row].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `job_${jobId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const goBack = () => {
    if (state?.from) {
      navigate(state.from, { replace: true });
      return;
    }
    navigate(buildBackHref(), { replace: true });
  };

  if (loading) {
    return (
      <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 text-sm text-gray-600">
        <div className="rounded-[24px] border border-gray-200 bg-white px-4 py-4 shadow-sm">Loading job...</div>
      </div>
    );
  }

  if (!jobDoc && !stopFromState) {
    return (
      <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-4 text-gray-900">
        <p className="text-sm text-red-600">Job not found.</p>
        <button
          onClick={goBack}
          className="inline-flex items-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-100 transition"
        >
          Back to history
        </button>
      </div>
    );
  }

  const safeAddress = addrToText(job.address) || "-";
  const safeSite =
    addrToText(job.siteLabel || job.siteName || job.address || job.customerName) || "-";

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-6 text-gray-900">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-baseline gap-2.5">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-600">
            Admin
          </span>
          <h1 className="text-lg font-semibold tracking-tight text-gray-950">Job details</h1>
        </div>
        <button
          onClick={goBack}
          className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50"
          title="Back to View History"
        >
          Back
        </button>
      </div>

      <div className="rounded-[24px] border border-gray-200 bg-white p-5 shadow-sm space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="text-sm">
            <span className="font-medium text-gray-700">Job ID: </span>
            <span className="text-gray-900">{jobId}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-700">Status: </span>
            <span className="text-gray-900">{job.status || "-"}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-700">Address: </span>
            <span className="text-gray-900">{safeAddress}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-700">Customer: </span>
            <span className="text-gray-900">{job.customerName || "-"}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-700">Site: </span>
            <span className="text-gray-900">{safeSite}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-700">PO number: </span>
            <span className="text-gray-900">{job.poNumber || "-"}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-700">Service: </span>
            <span className="text-gray-900">{serviceName || "-"}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-700">Price per bin: </span>
            <span className="text-gray-900">
              {serviceName ? (serviceHasPrice ? fmtCurrency(servicePrice) : "No price") : "-"}
            </span>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-700">Billable bins: </span>
            <span className="text-gray-900">{serviceName ? serviceBinQty : "-"}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-700">Service total: </span>
            <span className="text-gray-900">
              {serviceName ? (serviceHasPrice ? fmtCurrency(serviceTotalPrice) : "No price") : "-"}
            </span>
          </div>
          {isFutile && (
            <div className="text-sm">
              <span className="font-medium text-gray-700">FUTILE charge: </span>
              <span className="text-gray-900">{fmtCurrency(futileCharge)}</span>
            </div>
          )}
          <div className="text-sm">
            <span className="font-medium text-gray-700">Start trip: </span>
            <span className="text-gray-900">{fmtDateTime(job.startTripAt)}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-700">Job start: </span>
            <span className="text-gray-900">
              {fmtDateTime(job.startJobAt || job.arrivedAt)}
            </span>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-700">Finished: </span>
            <span className="text-gray-900">{fmtDateTime(job.completedTime)}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium text-gray-700">Loads logged: </span>
            <span className="text-gray-900">{emptyLoads.length}</span>
          </div>
        </div>

        {job.description && (
          <div
            className="text-sm text-gray-800"
            dangerouslySetInnerHTML={{
              __html: `<b>Description:</b> ${linkify(job.description)}`,
            }}
          />
        )}
        {jobDoc?.driverNotes && (
          <div
            className="text-sm text-gray-800"
            dangerouslySetInnerHTML={{
              __html: `<b>Driver notes:</b> ${linkify(jobDoc.driverNotes)}`,
            }}
          />
        )}
        {isFutile && (
          <div
            className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
            dangerouslySetInnerHTML={{
              __html: `<b>FUTILE note:</b> ${linkify(futileNote || "No note recorded.")}`,
            }}
          />
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Bins delivered" value={deliveredTotal} tone="emerald" />
        <MetricCard label="Bins picked up" value={pickedUpTotal} tone="blue" />
        <MetricCard label="Empty units" value={emptiedTotal} tone="amber" />
        <MetricCard
          label="Service total"
          value={serviceName ? (serviceHasPrice ? fmtCurrency(serviceTotalPrice) : "No price") : "-"}
          tone="slate"
        />
        {isFutile && (
          <MetricCard label="FUTILE charge" value={fmtCurrency(futileCharge)} tone="amber" />
        )}
        <MetricCard label="Photos" value={allPhotos.length} tone="slate" />
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Service details</h2>
          <p className="mt-1 text-sm text-gray-500">
            Pricing is treated as a per-bin rate. The total is calculated from the billable bin quantity saved on the job, or inferred from the recorded work when that value is not stored yet.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Service
            </div>
            <div className="mt-2 text-base font-semibold text-gray-900">
              {serviceName || "-"}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Price type
            </div>
            <div className="mt-2 text-base font-semibold text-gray-900">
              {serviceName ? (serviceHasPrice ? "Priced per bin" : "No-price service") : "-"}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Price per bin
            </div>
            <div className="mt-2 text-base font-semibold text-gray-900">
              {serviceName ? (serviceHasPrice ? fmtCurrency(servicePrice) : "No price") : "-"}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Billable bins
            </div>
            <div className="mt-2 text-base font-semibold text-gray-900">
              {serviceName ? serviceBinQty : "-"}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 md:col-span-2 xl:col-span-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Calculated total
            </div>
            <div className="mt-2 text-xl font-semibold text-gray-900">
              {serviceName ? (serviceHasPrice ? fmtCurrency(serviceTotalPrice) : "No price") : "-"}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Work completed</h2>
          <p className="text-sm text-gray-500 mt-1">
            Summary of what the driver recorded from Deliver, Pickup, and Empty cards.
          </p>
        </div>

        <div className="grid gap-5 xl:grid-cols-3">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-semibold text-emerald-950">Delivered bins</h3>
              <span className="rounded-full bg-white border border-emerald-200 px-3 py-1 text-sm font-semibold text-emerald-800">
                {deliveredTotal}
              </span>
            </div>
            <ItemList
              items={deliveries}
              emptyText="No delivered bins recorded for this job."
            />
          </div>

          <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-semibold text-blue-950">Picked up bins</h3>
              <span className="rounded-full bg-white border border-blue-200 px-3 py-1 text-sm font-semibold text-blue-800">
                {pickedUpTotal}
              </span>
            </div>
            <ItemList
              items={pickups}
              emptyText="No picked up bins recorded for this job."
            />
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-semibold text-amber-950">Emptied loads</h3>
              <span className="rounded-full bg-white border border-amber-200 px-3 py-1 text-sm font-semibold text-amber-800">
                {emptiedTotal}
              </span>
            </div>

            {materialSummary.length ? (
              <div className="space-y-2">
                {materialSummary.map((row) => (
                  <div
                    key={row.name}
                    className="flex items-center justify-between rounded-xl border border-amber-200 bg-white px-3 py-2"
                  >
                    <span className="text-sm font-medium text-gray-800">
                      {row.name}
                    </span>
                    <span className="text-sm font-semibold text-amber-900">
                      {row.units}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                No emptied material totals recorded for this job.
              </p>
            )}

            <div className="rounded-xl border border-amber-200 bg-white px-3 py-3 text-sm space-y-1">
              <div className="font-medium text-gray-800">Silica summary</div>
              <div className="flex justify-between">
                <span>Total weight (kg)</span>
                <span>{Math.round(asNumber(emptyLoadTotals?.silica?.weightKg) * 100) / 100}</span>
              </div>
              <div className="flex justify-between">
                <span>Total bags</span>
                <span>{asNumber(emptyLoadTotals?.silica?.bagCount)}</span>
              </div>
              <div className="flex justify-between">
                <span>Silica bins</span>
                <span>{asNumber(emptyLoadTotals?.silica?.binCount)}</span>
              </div>
              <div className="flex justify-between">
                <span>Total silica amount</span>
                <span>{fmtCurrency(asNumber(emptyLoadTotals?.silica?.amount))}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <button
          type="button"
          onClick={() => setEmptyLoadRecordsOpen((open) => !open)}
          disabled={!emptyLoads.length}
          aria-expanded={emptyLoadRecordsOpen}
          aria-controls="empty-load-records-list"
          className="flex w-full items-center justify-between gap-4 text-left disabled:cursor-default"
        >
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">Empty load records</h3>
            <p className="mt-1 text-sm text-gray-500">
              {emptyLoads.length} detailed {emptyLoads.length === 1 ? "record" : "records"} available
            </p>
          </div>
          <span className="shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700">
            {!emptyLoads.length
              ? "No records"
              : emptyLoadRecordsOpen
                ? "Hide records"
                : "Show records"}
          </span>
        </button>

        {emptyLoads.length ? (
          emptyLoadRecordsOpen && <div id="empty-load-records-list" className="mt-4 space-y-3 border-t border-gray-100 pt-4">
            {emptyLoads.map((load, idx) => {
              const materials = (Array.isArray(load.materials) ? load.materials : [])
                .map((m) => `${m.name || "Unknown"} ${asNumber(m.percent)}%`)
                .join(" · ");

              const isSilica = (Array.isArray(load.materials) ? load.materials : []).some(
                (m) => isSilicaMaterialName(m.name)
              );
              const hasCharge = asNumber(load.amount) > 0;

              return (
                <div
                  key={`${load.createdAt || idx}-${load.binId || idx}`}
                  className="rounded-2xl border border-gray-200 bg-gray-50 p-4"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">
                          {load.binName || "Bin not set"}
                        </span>
                        {load.binCategoryName && (
                          <span className="rounded-full bg-white border border-gray-200 px-2.5 py-0.5 text-xs font-semibold text-gray-700">
                            {load.binCategoryName}
                          </span>
                        )}
                        {load.overload && (
                          <span className="rounded-full bg-rose-100 border border-rose-200 px-2.5 py-0.5 text-xs font-semibold text-rose-700">
                            Overload
                          </span>
                        )}
                      </div>

                      <div className="grid gap-1 text-sm text-gray-600 md:grid-cols-2">
                        <div>
                          <span className="font-medium text-gray-700">Truck:</span>{" "}
                          {load.truckRego || "-"}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Load type:</span>{" "}
                          {load.loadType || "-"}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Units:</span>{" "}
                          {Math.round(asNumber(load.units) * 100) / 100}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Recorded:</span>{" "}
                          {fmtDateTime(load.createdAt)}
                        </div>
                      </div>

                      <div className="text-sm text-gray-700">
                        <span className="font-medium text-gray-800">Materials:</span>{" "}
                        {materials || "-"}
                      </div>

                      {isSilica && (
                        <div className="grid gap-1 text-sm text-gray-700 md:grid-cols-2">
                          <div>
                            <span className="font-medium text-gray-800">
                              Silica weight (kg):
                            </span>{" "}
                            {Math.round(asNumber(load.silicaWeightKg) * 100) / 100}
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              Bags count:
                            </span>{" "}
                            {asNumber(load.bagCount)}
                          </div>
                        </div>
                      )}

                      {hasCharge && (
                        <div className="grid gap-1 text-sm text-gray-700 md:grid-cols-2">
                          <div>
                            <span className="font-medium text-gray-800">
                              Charge quantity:
                            </span>{" "}
                            {asNumber(load.chargeQuantity)}{" "}
                            {load.chargeUnitLabel || "items"}
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              Charge amount:
                            </span>{" "}
                            {fmtCurrency(asNumber(load.amount))}
                          </div>
                        </div>
                      )}
                    </div>

                    {load.photoUrl ? (
                      <a
                        href={load.photoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-black transition"
                      >
                        View overload photo
                      </a>
                    ) : (
                      <div className="text-xs text-gray-500">
                        No overload photo attached
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mt-4 border-t border-gray-100 pt-4 text-sm text-gray-500">
            No empty load records found for this job.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={exportOneJobCSV}
          className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition"
        >
          Export job (CSV)
        </button>
        <button
          onClick={downloadServiceConfirmationPdf}
          disabled={confirmDownloading || !hasConfirmationPdf}
          className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition ${
            hasConfirmationPdf
              ? confirmDownloading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-violet-600 hover:bg-violet-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          {confirmDownloading
            ? "Downloading..."
            : "Download confirmation (PDF)"}
        </button>
        <button
          onClick={downloadAllPhotos}
          disabled={zipping || !allPhotos.length}
          className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition ${
            allPhotos.length
              ? zipping
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          {zipping ? "Preparing ZIP..." : "Download all photos (ZIP)"}
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Photos</h3>

        {allPhotos.length ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {allPhotos.map((p, i) => (
              <div key={i} className="group">
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open full size"
                >
                  <img
                    src={p.url}
                    alt={p.name || `Photo ${i + 1}`}
                    className="w-full h-32 object-cover rounded-xl border border-gray-200 group-hover:opacity-95"
                  />
                </a>

                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700">
                    {sourceLabel(p._source)}
                  </span>
                  {p.uploadedAt && (
                    <span className="text-[10px] text-gray-500 text-right">
                      {fmtDateTime(p.uploadedAt)}
                    </span>
                  )}
                </div>

                <div className="mt-2 flex flex-wrap gap-2">
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center bg-gray-900 hover:bg-black text-white text-xs px-3 py-1 rounded-lg transition"
                  >
                    Open
                  </a>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(p.url);
                        alert("Link copied");
                      } catch {
                        alert("Copy failed");
                      }
                    }}
                    className="inline-flex items-center justify-center bg-gray-700 hover:bg-gray-800 text-white text-xs px-3 py-1 rounded-lg transition"
                  >
                    Copy link
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 text-sm">
            No photos uploaded for this job.
          </p>
        )}
      </div>
    </div>
  );
}
